import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { LayoutService } from "./service/app.layout.service";
import { AppSidebarComponent } from "./app.sidebar.component";
import { AppTopBarComponent } from './app.topbar.component';
import { MaintenanceService } from '../@shared/maintenance.service';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { CeoAuthControllerService, HomeCompanyStatus } from '@api-swagger-client/index';
import { CredentialsService } from '../auth/credentials.service';
import { UserActions } from '@app/store/user/user.actions';

// Al cerrar la alerta no vuelve a salir en este lapso, para no estorbar en cada navegación.
const HOME_COMPANY_ALERT_SNOOZE_MS = 10 * 60 * 1000;
const HOME_COMPANY_ALERT_SNOOZE_KEY = 'homeCompanyAlertSnooze';

@Component({
    selector: 'app-layout',
    templateUrl: './app.layout.component.html'
})
export class AppLayoutComponent implements OnInit, OnDestroy {

    overlayMenuOpenSubscription: Subscription;

    // Alerta de mantenimiento
    showMaintenanceAlert = false;
    maintenanceAlertMessage = '';
    maintenanceScheduledAt = '';

    // Alerta de usuario fuera de su empresa base
    homeCompanyStatus: HomeCompanyStatus | null = null;
    showHomeCompanyAlert = false;
    isReturningHome = false;

    menuOutsideClickListener: any;

    profileMenuOutsideClickListener: any;

    @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

    @ViewChild(AppTopBarComponent) appTopbar!: AppTopBarComponent;

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private maintenanceService: MaintenanceService,
        private ceoAuthControllerService: CeoAuthControllerService,
        private credentialsService: CredentialsService,
        private store: Store,
        private messageService: MessageService,
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', event => {
                    const isOutsideClicked = !(this.appSidebar.el.nativeElement.isSameNode(event.target) || this.appSidebar.el.nativeElement.contains(event.target) 
                        || this.appTopbar.menuButton.nativeElement.isSameNode(event.target) || this.appTopbar.menuButton.nativeElement.contains(event.target));
                    
                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

        /*     if (!this.profileMenuOutsideClickListener) {
                this.profileMenuOutsideClickListener = this.renderer.listen('document', 'click', event => {
                    const isOutsideClicked = !(this.appTopbar.menu.nativeElement.isSameNode(event.target) || this.appTopbar.menu.nativeElement.contains(event.target)
                        || this.appTopbar.topbarMenuButton.nativeElement.isSameNode(event.target) || this.appTopbar.topbarMenuButton.nativeElement.contains(event.target));

                    if (isOutsideClicked) {
                        this.hideProfileMenu();
                    }
                });
            } */

            if (this.layoutService.state.staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.hideMenu();
                this.hideProfileMenu();
            });
    }

    ngOnInit(): void {
        this.maintenanceService.checkBackendStatus().subscribe((response) => {
            if (response.alert?.active) {
                this.showMaintenanceAlert = true;
                this.maintenanceAlertMessage = response.alert.message || 'Mantenimiento programado próximamente.';
                this.maintenanceScheduledAt = response.alert.scheduledAt || '';
            }
        });

        this.checkHomeCompanyStatus();
    }

    checkHomeCompanyStatus(): void {
        if (!this.credentialsService.isAuthenticated()) {
            return;
        }
        this.ceoAuthControllerService.ceoAuthControllerGetHomeCompanyStatus().subscribe({
            next: (response) => {
                this.homeCompanyStatus = response.data ?? null;
                this.showHomeCompanyAlert =
                    !!this.homeCompanyStatus?.isOutOfHomeCompany &&
                    !this.isHomeCompanyAlertSnoozed(this.homeCompanyStatus.currentCompanyId);
            },
            // Es solo un aviso: si falla no se interrumpe la navegación.
            error: () => (this.showHomeCompanyAlert = false),
        });
    }

    returnToHomeCompany(): void {
        this.isReturningHome = true;
        this.ceoAuthControllerService.ceoAuthControllerReturnHomeCompany().subscribe({
            next: (response) => {
                this.credentialsService.applyTokenResponse(response.data!);
                this.store.dispatch(UserActions.getCurrentUser());
                window.location.reload();
            },
            error: (error) => {
                this.isReturningHome = false;
                this.messageService.add({
                    key: 'topbar-tst',
                    severity: 'error',
                    summary: 'Error al regresar a tu empresa',
                    detail: error.error?.message || 'No se pudo completar el cambio',
                });
            },
        });
    }

    dismissHomeCompanyAlert(): void {
        this.showHomeCompanyAlert = false;
        try {
            localStorage.setItem(
                HOME_COMPANY_ALERT_SNOOZE_KEY,
                JSON.stringify({
                    companyId: this.homeCompanyStatus?.currentCompanyId,
                    until: Date.now() + HOME_COMPANY_ALERT_SNOOZE_MS,
                })
            );
        } catch {
            // Sin storage la alerta solo se oculta hasta la siguiente carga.
        }
    }

    // La pausa es por empresa: si se cambia a otra, el aviso vuelve a salir.
    private isHomeCompanyAlertSnoozed(companyId?: string): boolean {
        try {
            const snooze = JSON.parse(localStorage.getItem(HOME_COMPANY_ALERT_SNOOZE_KEY) || 'null');
            return !!snooze && snooze.companyId === companyId && Date.now() < snooze.until;
        } catch {
            return false;
        }
    }

    logoutFromHomeCompanyAlert(): void {
        this.showHomeCompanyAlert = false;
        this.appTopbar.logout();
    }

    hideMenu() {
        this.layoutService.state.overlayMenuActive = false;
        this.layoutService.state.staticMenuMobileActive = false;
        this.layoutService.state.menuHoverActive = false;
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    hideProfileMenu() {
        this.layoutService.state.profileSidebarVisible = false;
        if (this.profileMenuOutsideClickListener) {
            this.profileMenuOutsideClickListener();
            this.profileMenuOutsideClickListener = null;
        }
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        }
        else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        }
        else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
                'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-theme-light': this.layoutService.config().colorScheme === 'light',
            'layout-theme-dark': this.layoutService.config().colorScheme === 'dark',
            'layout-overlay': this.layoutService.config().menuMode === 'overlay',
            'layout-static': this.layoutService.config().menuMode === 'static',
            'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config().menuMode === 'static',
            'layout-overlay-active': this.layoutService.state.overlayMenuActive,
            'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
            'p-input-filled': this.layoutService.config().inputStyle === 'filled',
            'p-ripple-disabled': !this.layoutService.config().ripple
        }
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }
}
